/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { useKeycloakContext } from '../../../../../context/KeycloakContext';
import { EnumTipoAnexo } from '../../ts/enums/TipoAnexoEnum';
import { TNovidade } from '../../ts/types/Novidade';
import { CriarEtapaResponse } from '../../ts/types/ObrigacaoEtapa/Etapas/Etapa';
import {
	CriarObrigacaoPayload,
	CriarObrigacoesResponse,
	EditarObrigacoesEmMassaPayload,
	FiltroObrigacaoQuery,
	GetObrigacaoResponse,
} from '../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import { TOrigem } from '../../ts/types/ObrigacaoEtapa/Origem';
import {
	CriarAnexosResponse,
	EditarAnexosResponse,
	ExcluirAnexosResponse,
	ObterListaAnexosResponse,
} from '../../ts/types/ObrigacaoEtapa/common/Anexos';
import {
	CriarObrigacoesLegalResponse,
	EditarObrigacaoLegalResponse,
	ExcluirObrigacoesLegalResponse,
	TObrigacaoLegal,
} from '../../ts/types/ObrigacoesLegais';
import { CriarObrigacaoLegalFormSchema } from '../../zod/validations/CriarObrigacaoLegalZodSchema';
import { TApiResponse, TApiResponse2 } from '../helpers/ApiResponse';
import { ObrigacoesApi } from './api/ObrigacoesApi';
import { CriarNovidadeResponse, GetNovidadeResponse } from './types/Novidade/Novidade';
import { GetEstatisticasObrigacoesResponse } from './types/Obrigacoes/EstatisticasObrigacoes';
import { GetLogsObrigacaoResponse } from './types/Obrigacoes/LogsObrigacao';
import { GetObrigacoesLegaisResponse, GetObrigacoesResponse } from './types/Obrigacoes/Obrigacoes';
import { CriarUsuarioResponse, PartialTFuncionario } from './types/Obrigacoes/Usuarios';
import { getOrigemResponse } from './types/Origem/Origem';
import { GetUsuariosResponse } from './types/Usuarios/Usuarios';

export default class ObrigacoesService {
	private _obrigacaoApi: ObrigacoesApi;
	private _apiInstance: ReturnType<typeof axios.create>;

	constructor() {
		this._apiInstance = this._createApiInstance();
		this._obrigacaoApi = new ObrigacoesApi({ api: this._apiInstance });
	}

	private _createApiInstance() {
		const { accessToken } = useKeycloakContext();

		const apiInstance = axios.create({
			baseURL: 'https://calendarioabrapp-api-hml.jcmsystem.com.br/',
		});

		apiInstance.interceptors.request.use(
			(config) => {
				if (accessToken) {
					config.headers.Authorization = `Bearer ${accessToken}`;
				}
				return config;
			},
			(error) => {
				return Promise.reject(error);
			},
		);

		return apiInstance;
	}

	public async getAllObrigacoes(
		idEmpresa: number | string,
		options?: Record<string, string | number | boolean>,
	): Promise<GetObrigacoesResponse> {
		const response: TApiResponse<GetObrigacoesResponse> = await this._obrigacaoApi.getAllObrigacoes(idEmpresa, options);

		return response;
	}

	public async getAllObrigacoesEssenciais(
		idEmpresa: number | string,
		options?: Record<string, string | number | boolean>,
	): Promise<GetObrigacoesResponse> {
		const response: TApiResponse<GetObrigacoesResponse> = await this._obrigacaoApi.getAllObrigacoesEssenciais(
			idEmpresa,
			options,
		);

		return response;
	}

	public async getListagemObrigacoes(
		idEmpresa: number | string,
		filtros: FiltroObrigacaoQuery,
	): Promise<TApiResponse<GetObrigacoesResponse>> {
		const params = Object.fromEntries(
			Object.entries(filtros).filter(([_, v]) => v != null && v !== '' && v !== 'undefined'),
		) as unknown as FiltroObrigacaoQuery;

		return await this._obrigacaoApi.getListagemObrigacoes(idEmpresa, params);
	}

	public async getMinhasObrigacoes(
		idEmpresa: number | string,
		options?: Record<string, string | number | boolean>,
	): Promise<GetObrigacoesResponse> {
		const response: TApiResponse<GetObrigacoesResponse> = await this._obrigacaoApi.getMinhasObrigacoes(
			idEmpresa,
			options,
		);

		return response;
	}

	public async getMinhasEstatisticasObrigacoes(
		idEmpresa: number | string,
		options?: Record<string, string | number | boolean>,
	): Promise<GetEstatisticasObrigacoesResponse> {
		const response: TApiResponse<GetEstatisticasObrigacoesResponse> =
			await this._obrigacaoApi.getMinhasEstatisticasObrigacoes(idEmpresa, options);

		return response;
	}

	public async getAllEstatisticaObrigacoesEmpresa(
		idEmpresa: number | string,
		options?: Record<string, string | number | boolean>,
	): Promise<GetEstatisticasObrigacoesResponse> {
		const response: TApiResponse<GetEstatisticasObrigacoesResponse> =
			await this._obrigacaoApi.getAllEstatisticasObrigacoesEmpresa(idEmpresa, options);

		return response;
	}

	public async getUsuarioAutenticado(): Promise<GetUsuariosResponse> {
		const response: TApiResponse<GetUsuariosResponse> = await this._obrigacaoApi.getUsuarioAutenticado();

		return response.data.result;
	}

	public async getObrigacaoById(
		idEmpresa: number | string,
		idObrigacao: number | string,
	): Promise<GetObrigacaoResponse> {
		const response: TApiResponse<GetObrigacaoResponse> = await this._obrigacaoApi.getObrigacaoById(
			idEmpresa,
			idObrigacao,
		);
		return response.data.result;
	}

	public async criarObrigacao(
		idEmpresa: string | number,
		payload: unknown,
	): Promise<TApiResponse2<CriarObrigacoesResponse>> {
		return this._obrigacaoApi.criarObrigacao(idEmpresa, payload);
	}

	public async atualizarObrigacao(
		idEmpresa: string | number,
		idObrigacao: number | string,
		payload: CriarObrigacaoPayload,
	): Promise<TApiResponse2<CriarObrigacoesResponse>> {
		return this._obrigacaoApi.editarObrigacao(idEmpresa, idObrigacao, payload);
	}

	public async getAllUsuarios(
		idEmpresa: number | string,
		options?: Record<string, string | number | boolean>,
	): Promise<GetUsuariosResponse> {
		const response: TApiResponse<GetUsuariosResponse> = await this._obrigacaoApi.getAllUsuarios(idEmpresa, options);

		return response.data.result;
	}

	public async getAllUsuariosTodos(
		idEmpresa: number | string,
		currentPage = 1,
		pageSize = 10,
	): Promise<GetUsuariosResponse> {
		const response: TApiResponse<GetUsuariosResponse> = await this._obrigacaoApi.getAllUsuariosTodos(idEmpresa, {
			currentPage,
			pageSize,
		});

		return response.data.result;
	}

	public async getFuncionariosByEmpresa(
		idEmpresa: number | string,
		options?: Record<string, unknown>,
	): Promise<GetUsuariosResponse> {
		const response = await this._obrigacaoApi.getAllUsuarios(idEmpresa, options);

		if (response.data && response.data.result) {
			return response.data.result;
		} else {
			throw new Error('A resposta da API não contém a propriedade "result" com a lista de funcionários.');
		}
	}

	public async criarAnexoObrigacao(
		idEmpresa: number | string,
		idObrigacao: number | string,
		arquivo: File,
		descricao: string,
		tipoAnexo: EnumTipoAnexo,
		username: string,
	): Promise<TApiResponse<CriarAnexosResponse>> {
		const formData = new FormData();
		formData.append('Arquivo', arquivo);
		formData.append('Descricao', descricao);
		formData.append('Username', username);
		const response = await this._obrigacaoApi.criarAnexoObrigacao(idEmpresa, idObrigacao, formData, tipoAnexo);
		return response;
	}

	public async editarAnexoObrigacao(
		idEmpresa: number | string,
		idObrigacao: string | number | null,
		idAnexo: number | string,
		descricao: string,
	): Promise<TApiResponse<EditarAnexosResponse>> {
		const formData = new FormData();
		formData.append('Descricao', descricao);

		return this._obrigacaoApi.editarAnexoObrigacao(idEmpresa, idObrigacao, idAnexo, formData);
	}

	public async excluirAnexoObrigacao(
		idEmpresa: number | string,
		idObrigacao: number | string,
		idAnexo: number | string,
		tipoAnexo: EnumTipoAnexo,
	): Promise<TApiResponse<ExcluirAnexosResponse>> {
		return this._obrigacaoApi.excluirAnexoObrigacao(idEmpresa, idObrigacao, idAnexo, tipoAnexo);
	}

	public async getListaAnexos(
		idEmpresa: number | string,
		idObrigacao: number | string,
	): Promise<Array<ObterListaAnexosResponse>> {
		try {
			const response: TApiResponse2<Array<ObterListaAnexosResponse>> = await this._obrigacaoApi.getListaAnexos(
				idEmpresa,
				idObrigacao,
			);

			return response.data;
		} catch (error) {
			console.error('getListaAnexos - Error:', error);
			throw error;
		}
	}

	public async downloadAnexoObrigacao(
		idEmpresa: number | string,
		idAnexo: number | string,
		nomeOriginal: string,
	): Promise<void> {
		try {
			await this._obrigacaoApi.downloadAnexoObrigacao(idEmpresa, idAnexo, nomeOriginal);
		} catch (error) {
			console.error('Erro ao baixar o anexo:', error);
			throw error;
		}
	}

	public async getAllOrigem(idEmpresa: number | string): Promise<getOrigemResponse> {
		try {
			const response: TApiResponse<getOrigemResponse> = await this._obrigacaoApi.getAllOrigem(idEmpresa);

			return response.data.result;
		} catch (error) {
			console.error('getListaAnexos - Error:', error);
			throw error;
		}
	}

	public async criarOrigem(idEmpresa: string | number, payload: TOrigem): Promise<TApiResponse<TOrigem>> {
		return this._obrigacaoApi.criarOrigem(idEmpresa, payload);
	}

	public async atualizarOrigem(
		idEmpresa: string | number,
		idOrigem: number | string,
		payload: TOrigem,
	): Promise<TApiResponse<TOrigem>> {
		return this._obrigacaoApi.atualizarOrigem(idEmpresa, idOrigem, payload);
	}

	public async deletarOrigem(idEmpresa: string | number, idOrigem: number | string): Promise<TApiResponse<TOrigem>> {
		return this._obrigacaoApi.deletarOrigem(idEmpresa, idOrigem);
	}

	public async getEmpresasListagem(idEmpresa: number | string): Promise<any> {
		const response: TApiResponse2<any> = await this._obrigacaoApi.getEmpresasListagem(idEmpresa);
		return response.data;
	}

	public async updateEmpresaAtiva(idEmpresa: number | string, idUsuario: number): Promise<TApiResponse<any>> {
		const idEmpresaNumerico = typeof idEmpresa === 'string' ? parseInt(idEmpresa, 10) : idEmpresa;
		return await this._obrigacaoApi.updateEmpresaAtiva(idEmpresaNumerico, {
			idEmpresa: idEmpresaNumerico,
			idUsuario,
		});
	}

	public async criarUsuario(idEmpresa: string | number, payload: unknown): Promise<TApiResponse<CriarUsuarioResponse>> {
		return this._obrigacaoApi.criarUsuario(idEmpresa, payload);
	}

	public async atualizarUsuario(
		idEmpresa: string | number,
		idUsuario: number | string,
		payload: PartialTFuncionario,
	): Promise<TApiResponse<PartialTFuncionario>> {
		return this._obrigacaoApi.atualizarUsuario(idEmpresa, idUsuario, payload);
	}

	public async getLogsObrigacao(idEmpresa: number | string, idLog: number | string): Promise<GetLogsObrigacaoResponse> {
		return await this._obrigacaoApi.getAllObrigacaoLogs(idEmpresa, idLog);
	}

	public async getAllNovidades(idEmpresa: number | string): Promise<GetNovidadeResponse> {
		return await this._obrigacaoApi.getAllNovidades(idEmpresa);
	}

	public async editarObrigacoesEmMassa(
		idEmpresa: string | number,
		payload: EditarObrigacoesEmMassaPayload,
	): Promise<TApiResponse<null>> {
		try {
			const response = await this._obrigacaoApi.editarObrigacoesEmMassa(idEmpresa, payload);
			if (!response.isSuccess) {
				const errorDetail = response.errors ?? 'Erro desconhecido.';
				console.error('Erro na API:', errorDetail);
				throw new Error(`Erro na API: ${errorDetail}`);
			}
			return response;
		} catch (error) {
			console.error('Erro ao realizar edição em massa:', error);
			throw error;
		}
	}

	public async getAllLogs(idEmpresa: number | string): Promise<GetLogsObrigacaoResponse> {
		return await this._obrigacaoApi.getAllLogs(idEmpresa);
	}

	public async excluirObrigacoesEmMassa(idEmpresa: number | string, idsObrigacoes: number[]): Promise<void> {
		try {
			const payload = { idsObrigacoes };
			const response = await this._obrigacaoApi.excluirObrigacoesEmMassa(idEmpresa, payload);

			if (!response.isSuccess) {
				throw new Error('Erro ao excluir obrigações em massa.');
			}
		} catch (error) {
			console.error('Erro ao excluir obrigações:', error);
			throw error;
		}
	}

	public async registrarLogin(idEmpresa: string | number): Promise<void> {
		return this._obrigacaoApi.registrarLogin(idEmpresa);
	}

	public async criarNovidade(
		idEmpresa: string | number,
		payload: TNovidade,
	): Promise<TApiResponse<CriarNovidadeResponse>> {
		return this._obrigacaoApi.criarNovidade(idEmpresa, payload);
	}

	public async deletarNovidade(
		idEmpresa: string | number,
		idNovidade: number | string,
	): Promise<TApiResponse<TNovidade>> {
		return this._obrigacaoApi.deletarNovidade(idEmpresa, idNovidade);
	}

	public async getTodasObrigacoesLegais(idEmpresa: number | string): Promise<GetObrigacoesLegaisResponse> {
		const response: TApiResponse<GetObrigacoesLegaisResponse> =
			await this._obrigacaoApi.getTodasObrigacoesLegais(idEmpresa);

		return response;
	}

	public async postObrigacaoLegal(
		idEmpresa: string | number,
		payload: TObrigacaoLegal[],
	): Promise<TApiResponse<CriarObrigacoesLegalResponse>> {
		return this._obrigacaoApi.postCriarObrigacoesLegais(idEmpresa, payload);
	}

	public async putObrigacaoLegal(
		idEmpresa: string | number,
		idObrigacao: number | string,
		payload: CriarObrigacaoLegalFormSchema,
	): Promise<TApiResponse<EditarObrigacaoLegalResponse>> {
		return this._obrigacaoApi.putObrigacaoLegal(idEmpresa, idObrigacao, payload);
	}

	public async putAtualizarObrigacoesAbrappEmMassa(
		idEmpresa: string | number,
		codigoObrigacao: number | string,
	): Promise<TApiResponse<CriarObrigacoesLegalResponse>> {
		return this._obrigacaoApi.putAtualizarObrigacoesAbrappEmMassa(idEmpresa, codigoObrigacao, null);
	}

	public async putCriarObrigacoesAbrappEmMassa(
		idEmpresa: string | number,
		codigoObrigacao: number | string,
	): Promise<TApiResponse<CriarObrigacoesLegalResponse>> {
		return this._obrigacaoApi.putCriarObrigacoesAbrappEmMassa(idEmpresa, codigoObrigacao, null);
	}

	public async excluirObrigacaoLegalAbrapp(
		idEmpresa: string | number,
		idObrigacao: number | string,
	): Promise<TApiResponse<ExcluirObrigacoesLegalResponse>> {
		try {
			const response = await this._obrigacaoApi.deleteObrigacaoLegalAbrapp(idEmpresa, idObrigacao);

			return response;
		} catch (error) {
			console.error('Erro ao excluir obrigação legal Abrapp:', error);
			throw error;
		}
	}

	public async criarEtapa(idEmpresa: string | number, payload: unknown): Promise<TApiResponse<CriarEtapaResponse>> {
		return this._obrigacaoApi.criarEtapa(idEmpresa, payload);
	}

	public async atualizarEtapa(
		idEmpresa: string | number,
		idEtapa: number | string,
		payload: CriarObrigacaoPayload,
	): Promise<TApiResponse<CriarObrigacoesResponse>> {
		return this._obrigacaoApi.editarEtapa(idEmpresa, idEtapa, payload);
	}
}
